'use client';

import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname, useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';
import { LayoutDashboard, Clock, Calendar, FileText, DollarSign, UserPlus, ShieldCheck, ChevronLeft, ChevronRight, Settings, LogOut, UserCircle, ClipboardList, BarChart3, ClipboardCheck, Download, CheckCircle, X } from 'lucide-react';
import Icon from '@/components/ui/AppIcon';
import { useAuth } from '@/contexts/AuthContext';
import { usePWAInstall } from '@/hooks/usePWAInstall';


interface NavItem {
  id: string;
  label: string;
  href: string;
  icon: React.ElementType;
  badge?: number;
  group: string;
}

const navItems: NavItem[] = [
  { id: 'nav-dashboard', label: 'Dashboard', href: '/', icon: LayoutDashboard, group: 'main' },
  { id: 'nav-time-tracking', label: 'Time Tracking', href: '/time-tracking', icon: Clock, badge: 3, group: 'operations' },
  { id: 'nav-scheduling', label: 'Scheduling', href: '/scheduling', icon: Calendar, badge: 7, group: 'operations' },
  { id: 'nav-invoicing', label: 'Invoicing', href: '/invoicing', icon: FileText, badge: 2, group: 'finance' },
  { id: 'nav-payroll', label: 'Payroll', href: '/payroll', icon: DollarSign, group: 'finance' },
  { id: 'nav-reports', label: 'Reports', href: '/reports', icon: BarChart3, group: 'finance' },
  { id: 'nav-hiring', label: 'Hiring', href: '/hiring', icon: UserPlus, badge: 12, group: 'people' },
  { id: 'nav-onboarding', label: 'Onboarding', href: '/onboarding', icon: ClipboardCheck, group: 'people' },
  { id: 'nav-credentials', label: 'Credentials', href: '/credentials', icon: ShieldCheck, badge: 5, group: 'people' },
  { id: 'nav-my-profile', label: 'My Profile', href: '/my-profile', icon: UserCircle, group: 'people' },
  { id: 'nav-audit', label: 'Audit Log', href: '/audit', icon: ClipboardList, group: 'compliance' },
];

const groups = [
  { id: 'main', label: null },
  { id: 'operations', label: 'Operations' },
  { id: 'finance', label: 'Finance' },
  { id: 'people', label: 'People' },
  { id: 'compliance', label: 'Compliance' },
];

interface SidebarProps {
  collapsed: boolean;
  onToggle: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

export default function Sidebar({ collapsed, onToggle, mobileOpen, onMobileClose }: SidebarProps) {
  const pathname = usePathname();
  const router = useRouter();
  const { signOut } = useAuth();
  const { isInstallable, isInstalled, install } = usePWAInstall();

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (_) {
      // ignore sign-out errors
    } finally {
      router.push('/login');
    }
  };

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const handleNavClick = () => {
    // Close mobile drawer when a nav item is clicked
    onMobileClose();
  };

  return (
    <>
      {/* Mobile overlay */}
      {mobileOpen && (
        <div
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 40 }}
          className="md:hidden"
          onClick={onMobileClose}
          aria-hidden="true"
        />
      )}

      <aside
        className={`fixed top-0 left-0 h-full bg-card border-r border-border flex flex-col sidebar-transition
            ${collapsed ? 'md:w-16' : 'md:w-60'}
            w-60
            ${mobileOpen ? 'translate-x-0' : '-translate-x-full md:translate-x-0'}
          `}
        style={{ zIndex: 41 }}
      >
        {/* Logo */}
        <div className={`flex items-center border-b border-border h-[60px] px-3 ${collapsed ? 'md:justify-center justify-between' : 'justify-between'}`}>
          <div className="flex items-center gap-2 min-w-0">
            <AppLogo size={32} />
            <span className={`font-semibold text-foreground text-[15px] tracking-tight truncate ${collapsed ? 'md:hidden' : ''}`}>
              MedStaff
            </span>
          </div>
          {/* Desktop collapse button */}
          {!collapsed && (
            <button
              onClick={onToggle}
              className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors flex-shrink-0 hidden md:flex"
              aria-label="Collapse sidebar"
            >
              <ChevronLeft size={16} />
            </button>
          )}
          {/* Mobile close button */}
          <button
            onClick={onMobileClose}
            className="p-1.5 rounded-md hover:bg-muted text-muted-foreground transition-colors flex-shrink-0 md:hidden"
            aria-label="Close menu"
          >
            <X size={16} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 overflow-y-auto scrollbar-thin py-3 px-2">
          {groups.map((group) => {
            const items = navItems.filter((item) => item.group === group.id);
            if (items.length === 0) return null;
            return (
              <div key={`group-${group.id}`} className="mb-4">
                {group.label && !collapsed && (
                  <p className="text-[10px] font-600 uppercase tracking-widest text-muted-foreground px-2 mb-1.5">
                    {group.label}
                  </p>
                )}
                {group.label && collapsed && <div className="border-t border-border mx-1 mb-2 hidden md:block" />}
                <ul className="space-y-0.5">
                  {items.map((item) => {
                    const Icon = item.icon;
                    const active = isActive(item.href);
                    return (
                      <li key={item.id}>
                        <Link
                          href={item.href}
                          onClick={handleNavClick}
                          className={`nav-item flex items-center gap-3 px-2.5 py-2 text-sm font-medium relative group ${
                            active
                              ? 'nav-active' : 'text-muted-foreground hover:text-foreground'
                          } ${collapsed ? 'md:justify-center' : ''}`}
                          title={collapsed ? item.label : undefined}
                        >
                          <Icon size={18} className="flex-shrink-0" />
                          <span className={`truncate flex-1 ${collapsed ? 'md:hidden' : ''}`}>{item.label}</span>
                          {item.badge && item.badge > 0 && (
                            <span className={`ml-auto bg-primary text-primary-foreground text-[10px] font-600 rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1 ${collapsed ? 'md:hidden' : ''}`}>
                              {item.badge}
                            </span>
                          )}
                          {collapsed && item.badge && item.badge > 0 && (
                            <span className="absolute top-1 right-1 bg-danger text-white text-[9px] font-700 rounded-full w-4 h-4 items-center justify-center hidden md:flex">
                              {item.badge > 9 ? '9+' : item.badge}
                            </span>
                          )}
                          {/* Tooltip for collapsed state — desktop only */}
                          {collapsed && (
                            <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 hidden md:block">
                              {item.label}
                              {item.badge ? ` (${item.badge})` : ''}
                            </div>
                          )}
                        </Link>
                      </li>
                    );
                  })}
                </ul>
              </div>
            );
          })}
        </nav>

        {/* Bottom section */}
        <div className="border-t border-border p-2 space-y-0.5">
          {collapsed && (
            <button
              onClick={onToggle}
              className="nav-item w-full items-center justify-center p-2.5 text-muted-foreground hover:text-foreground group relative hidden md:flex"
              aria-label="Expand sidebar"
            >
              <ChevronRight size={18} />
              <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50">
                Expand sidebar
              </div>
            </button>
          )}
          <Link
            href="/settings"
            onClick={handleNavClick}
            className={`nav-item flex items-center gap-3 px-2.5 py-2 text-sm font-medium group relative ${
              isActive('/settings')
                ? 'nav-active' : 'text-muted-foreground hover:text-foreground'
            } ${collapsed ? 'md:justify-center' : ''}`}
          >
            <Settings size={18} className="flex-shrink-0" />
            <span className={collapsed ? 'md:hidden' : ''}>Settings</span>
            {collapsed && (
              <div className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded-md whitespace-nowrap opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity z-50 hidden md:block">
                Settings
              </div>
            )}
          </Link>

          {/* User profile */}
          <div className={`relative group/user ${collapsed ? 'md:flex md:justify-center' : ''}`}>
            <div className={`flex items-center gap-2.5 px-2 py-2 rounded-lg hover:bg-muted cursor-pointer transition-colors ${collapsed ? 'md:justify-center' : ''}`}>
              <div className="w-7 h-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center text-xs font-600 flex-shrink-0">
                AK
              </div>
              <div className={`flex-1 min-w-0 ${collapsed ? 'md:hidden' : ''}`}>
                <p className="text-xs font-600 text-foreground truncate">Arjun Kapoor</p>
                <p className="text-[10px] text-muted-foreground truncate">Admin</p>
              </div>
            </div>

            {/* Hover popout menu */}
            <div
              className={`absolute bottom-full mb-1 bg-card border border-border rounded-lg shadow-lg py-1 min-w-[160px] opacity-0 pointer-events-none group-hover/user:opacity-100 group-hover/user:pointer-events-auto transition-opacity z-50 ${
                collapsed ? 'md:left-full md:ml-2 md:bottom-0 md:mb-0 left-0' : 'left-0'
              }`}
            >
              <div className="px-3 py-1.5 border-b border-border mb-1">
                <p className="text-xs font-600 text-foreground">Arjun Kapoor</p>
                <p className="text-[10px] text-muted-foreground">Admin</p>
              </div>
              {/* PWA Install button */}
              {!isInstalled && isInstallable && (
                <button
                  onClick={install}
                  className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-primary hover:bg-primary/5 transition-colors"
                >
                  <Download size={14} />
                  <span>Install App</span>
                </button>
              )}
              {isInstalled && (
                <div className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground cursor-default">
                  <CheckCircle size={14} className="text-success" />
                  <span>App Installed</span>
                </div>
              )}
              <button
                onClick={handleLogout}
                className="w-full flex items-center gap-2 px-3 py-1.5 text-sm text-muted-foreground hover:text-danger hover:bg-danger/5 transition-colors"
              >
                <LogOut size={14} />
                <span>Log out</span>
              </button>
            </div>
          </div>
        </div>
      </aside>
    </>
  );
}